import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Subscription {
  id: string;
  user_id: string;
  subscription_type: 'new_post' | 'new_comment' | 'comment_reply';
  post_id: string | null;
  enabled: boolean;
}

export const useSubscriptions = (userId?: string) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSubscriptions = async () => {
    if (!userId) {
      setSubscriptions([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;

      setSubscriptions((data || []) as Subscription[]);
    } catch (error: any) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [userId]);

  const getSubscription = (type: 'new_post' | 'new_comment' | 'comment_reply', postId?: string): Subscription | undefined => {
    return subscriptions.find(
      (sub) =>
        sub.subscription_type === type &&
        sub.enabled &&
        (postId ? sub.post_id === postId : sub.post_id === null)
    );
  };

  const isSubscribed = (type: 'new_post' | 'new_comment' | 'comment_reply', postId?: string): boolean => {
    return !!getSubscription(type, postId);
  };

  const subscribe = async (type: 'new_post' | 'new_comment' | 'comment_reply', postId?: string) => {
    if (!userId) {
      toast.error('Please sign in to subscribe');
      return false;
    }

    try {
      // Check if subscription exists first
      let query = supabase
        .from('subscriptions')
        .select('id')
        .eq('user_id', userId)
        .eq('subscription_type', type);
      
      if (postId) {
        query = query.eq('post_id', postId);
      } else {
        query = query.is('post_id', null);
      }
      
      const { data: existing } = await query.single();

      if (existing) {
        // Update existing subscription
        const { error } = await supabase
          .from('subscriptions')
          .update({ enabled: true })
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        // Insert new subscription
        const { error } = await supabase
          .from('subscriptions')
          .insert({
            user_id: userId,
            subscription_type: type,
            post_id: postId || null,
            enabled: true,
          });

        if (error) throw error;
      }

      await fetchSubscriptions();
      
      const messages = {
        'new_post': 'You\'ll be notified of new blog posts!',
        'new_comment': 'You\'ll be notified of new comments on this post!',
        'comment_reply': 'You\'ll be notified of replies to your comments!',
      };
      
      toast.success(messages[type]);
      return true;
    } catch (error: any) {
      console.error('Error subscribing:', error);
      toast.error('Failed to subscribe');
      return false;
    }
  };

  const unsubscribe = async (type: 'new_post' | 'new_comment' | 'comment_reply', postId?: string) => {
    if (!userId) return false;

    try {
      let query = supabase
        .from('subscriptions')
        .update({ enabled: false })
        .eq('user_id', userId)
        .eq('subscription_type', type);
      
      // Handle NULL vs specific post_id
      if (postId) {
        query = query.eq('post_id', postId);
      } else {
        query = query.is('post_id', null);
      }
      
      const { error } = await query;

      if (error) throw error;

      await fetchSubscriptions();
      toast.success('Unsubscribed successfully');
      return true;
    } catch (error: any) {
      console.error('Error unsubscribing:', error);
      toast.error('Failed to unsubscribe');
      return false;
    }
  };

  const toggleSubscription = async (type: 'new_post' | 'new_comment' | 'comment_reply', postId?: string) => {
    const existing = getSubscription(type, postId);
    if (existing) {
      return await unsubscribe(type, postId);
    } else {
      return await subscribe(type, postId);
    }
  };

  return {
    subscriptions,
    loading,
    isSubscribed,
    getSubscription,
    subscribe,
    unsubscribe,
    toggleSubscription,
    refetch: fetchSubscriptions,
  };
};